import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, Menu, X, Heart, MessageCircle, User, LogOut, ChevronDown, Settings, LayoutDashboard, Shield, Bell } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../lib/api';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

export function Navbar() {
  const { user, logout, isAuthenticated, isSeller, isAdmin } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      api.getNotifications(true).then(d => setUnreadCount(d.unread_count)).catch(() => {});
    }
  }, [isAuthenticated]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <nav 
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-[#0A0A0C]/95 backdrop-blur-xl border-b border-white/5' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <img src="/logo64.png" alt="MorphMX" className="w-8 h-8 md:w-10 md:h-10" />
            <span className="font-semibold text-xl text-zinc-100 hidden sm:block">MorphMX</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            <Link to="/explorar" className="text-zinc-400 hover:text-zinc-100 transition-colors text-sm font-medium" data-testid="nav-explore">
              Explorar
            </Link>
            <Link to="/categorias" className="text-zinc-400 hover:text-zinc-100 transition-colors text-sm font-medium" data-testid="nav-categories">
              Categorías
            </Link>
            <Link to="/criadores" className="text-zinc-400 hover:text-zinc-100 transition-colors text-sm font-medium" data-testid="nav-breeders">
              Criadores
            </Link>
            <Link to="/subastas" className="text-zinc-400 hover:text-zinc-100 transition-colors text-sm font-medium" data-testid="nav-auctions">
              Subastas
            </Link>
            <Link to="/precios" className="text-zinc-400 hover:text-zinc-100 transition-colors text-sm font-medium" data-testid="nav-pricing">
              Precios
            </Link>
          </div>

          {/* Right Actions */}
          <div className="flex items-center gap-3">
            {isAuthenticated ? (
              <>
                <Link to="/favoritos" className="p-2 text-zinc-400 hover:text-zinc-100 transition-colors" data-testid="nav-favorites">
                  <Heart className="w-5 h-5" />
                </Link>
                <Link to="/mensajes" className="p-2 text-zinc-400 hover:text-zinc-100 transition-colors" data-testid="nav-messages">
                  <MessageCircle className="w-5 h-5" />
                </Link>
                <Link to="/dashboard" className="p-2 text-zinc-400 hover:text-zinc-100 transition-colors relative" data-testid="nav-notifications">
                  <Bell className="w-5 h-5" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-red-500 text-[10px] text-white flex items-center justify-center">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </Link>
                
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="flex items-center gap-2 text-zinc-400 hover:text-zinc-100" data-testid="user-menu-trigger">
                      <div className="w-8 h-8 rounded-full bg-zinc-800 flex items-center justify-center">
                        {user?.avatar_url ? (
                          <img src={user.avatar_url} alt="" className="w-full h-full rounded-full object-cover" />
                        ) : (
                          <User className="w-4 h-4" />
                        )}
                      </div>
                      <ChevronDown className="w-4 h-4 hidden sm:block" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56 bg-[#121215] border-white/10">
                    <div className="px-3 py-2 border-b border-white/5">
                      <p className="text-sm font-medium text-zinc-100">{user?.name}</p>
                      <p className="text-xs text-zinc-500">{user?.email}</p>
                    </div>
                    {isSeller && (
                      <DropdownMenuItem asChild>
                        <Link to="/dashboard" className="flex items-center gap-2 cursor-pointer" data-testid="menu-dashboard">
                          <LayoutDashboard className="w-4 h-4" />
                          Dashboard
                        </Link>
                      </DropdownMenuItem>
                    )}
                    {!isSeller && (
                      <DropdownMenuItem asChild>
                        <Link to="/dashboard" className="flex items-center gap-2 cursor-pointer" data-testid="menu-dashboard">
                          <LayoutDashboard className="w-4 h-4" />
                          Mi cuenta
                        </Link>
                      </DropdownMenuItem>
                    )}
                    {isAdmin && (
                      <DropdownMenuItem asChild>
                        <Link to="/admin" className="flex items-center gap-2 cursor-pointer" data-testid="menu-admin">
                          <Shield className="w-4 h-4" />
                          Admin
                        </Link>
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem asChild>
                      <Link to="/dashboard/configuracion" className="flex items-center gap-2 cursor-pointer" data-testid="menu-profile">
                        <Settings className="w-4 h-4" />
                        Configuración
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="bg-white/5" />
                    <DropdownMenuItem onClick={handleLogout} className="flex items-center gap-2 cursor-pointer text-red-400" data-testid="menu-logout">
                      <LogOut className="w-4 h-4" />
                      Cerrar sesión
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" className="text-zinc-400 hover:text-zinc-100" data-testid="nav-login">
                    Iniciar sesión
                  </Button>
                </Link>
                <Link to="/registro">
                  <Button className="bg-zinc-100 text-zinc-900 hover:bg-white hidden sm:flex" data-testid="nav-register">
                    Registrarse
                  </Button>
                </Link>
              </>
            )}

            {/* Mobile menu toggle */}
            <button 
              className="md:hidden p-2 text-zinc-400"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 top-16 bg-[#0A0A0C]/98 backdrop-blur-xl z-40" data-testid="mobile-menu">
          <div className="p-6 space-y-4">
            <Link to="/explorar" className="block py-3 text-lg text-zinc-100" onClick={() => setMobileMenuOpen(false)}>
              Explorar
            </Link>
            <Link to="/categorias" className="block py-3 text-lg text-zinc-100" onClick={() => setMobileMenuOpen(false)}>
              Categorías
            </Link>
            <Link to="/criadores" className="block py-3 text-lg text-zinc-100" onClick={() => setMobileMenuOpen(false)}>
              Criadores
            </Link>
            <Link to="/subastas" className="block py-3 text-lg text-zinc-100" onClick={() => setMobileMenuOpen(false)}>
              Subastas
            </Link>
            <Link to="/precios" className="block py-3 text-lg text-zinc-100" onClick={() => setMobileMenuOpen(false)}>
              Precios
            </Link>
            {!isAuthenticated && (
              <div className="pt-4 space-y-3">
                <Link to="/login" className="block">
                  <Button variant="outline" className="w-full" onClick={() => setMobileMenuOpen(false)}>
                    Iniciar sesión
                  </Button>
                </Link>
                <Link to="/registro" className="block">
                  <Button className="w-full bg-zinc-100 text-zinc-900" onClick={() => setMobileMenuOpen(false)}>
                    Registrarse
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
